/**
 * The admin's exercise list (B-66, D-118): add an exercise, rename any exercise, and edit
 * an added one's other names. Shown on Profile to the admin's number only — and that is
 * presentation, not protection: firestore.rules refuses every write from anyone else.
 *
 * What it deliberately cannot do is change a factor, or reps-versus-time, once an
 * exercise exists. Both are chosen once, here, with a sentence saying so beside them.
 */

import { useMemo, useState } from "react";
import type { CatalogEdits, CatalogExercise, Measure, MuscleGroup } from "@altrtrack/core";
import {
  BUILT_IN_CATALOG,
  CATALOG,
  MAX_DURATION_FACTOR,
  MAX_FACTOR,
  MUSCLE_GROUPS,
  isCustomId,
  normalise,
  prepareNewExercise,
  prepareRename,
} from "@altrtrack/core";
import { useCatalogRevision } from "../data.js";
import { addCustomExercise, renameExercise, restoreExerciseName } from "../write.js";
import { NumberField } from "./NumberField.js";
import { GROUP_NAMES } from "./ExercisePicker.js";

/** "meadows row, landmine rows" → two phrases. */
function splitNames(text: string): string[] {
  return text
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

export function ExerciseAdmin({ uid, edits }: { uid: string; edits: CatalogEdits }) {
  return (
    <>
      <AddExercise uid={uid} />
      <RenameExercise uid={uid} edits={edits} />
      <AddedList edits={edits} />
    </>
  );
}

function Problems({ problems }: { problems: string[] }) {
  if (problems.length === 0) return null;
  return (
    <div className="notice bad" role="alert">
      <b>Not saved.</b>
      {problems.map((p) => (
        <p key={p} className="sub">
          {p}
        </p>
      ))}
    </div>
  );
}

function AddExercise({ uid }: { uid: string }) {
  const [name, setName] = useState("");
  const [group, setGroup] = useState<MuscleGroup>("chest");
  const [measure, setMeasure] = useState<Measure>("reps");
  const [factor, setFactor] = useState(0);
  const [aliases, setAliases] = useState("");
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const ceiling = measure === "duration" ? MAX_DURATION_FACTOR : MAX_FACTOR;

  const add = async () => {
    setDone(null);
    const prepared = prepareNewExercise({
      name,
      muscleGroup: group,
      bodyweightFactor: factor,
      measure,
      aliases: splitNames(aliases),
    });
    if (!prepared.ok) {
      setProblems(prepared.problems);
      return;
    }
    setSaving(true);
    const result = await addCustomExercise(uid, prepared.exercise);
    setSaving(false);
    if (!result.ok) {
      setProblems([result.reason === "failed" ? result.detail : "Could not save."]);
      return;
    }
    setProblems([]);
    setDone(prepared.exercise.name);
    setName("");
    setAliases("");
    setFactor(0);
  };

  return (
    <div className="admin-block">
      <h3 className="label">Add an exercise</h3>
      <label className="field">
        <span className="label">Name</span>
        <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
      </label>
      <div className="field-row">
        <label className="field">
          <span className="label">Muscle group</span>
          <select className="select" value={group} onChange={(e) => setGroup(e.target.value as MuscleGroup)}>
            {MUSCLE_GROUPS.map((g) => (
              <option key={g} value={g}>
                {GROUP_NAMES[g]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Counted in</span>
          <select className="select" value={measure} onChange={(e) => setMeasure(e.target.value as Measure)}>
            <option value="reps">Reps</option>
            <option value="duration">Time</option>
          </select>
        </label>
      </div>
      <label className="field">
        <span className="label">Bodyweight factor</span>
        <NumberField
          className="input input-narrow"
          value={factor}
          bounds={{ min: 0, max: ceiling, unit: "factor" }}
          label="Bodyweight factor, from 0 to 1"
          onCommit={setFactor}
        />
      </label>
      <p className="sub">
        0 for machine and free-weight work; the share of the body lifted otherwise — a push-up
        is 0.65, a pull-up 1. At most {ceiling} for {measure === "duration" ? "a timed exercise" : "reps"}.{" "}
        <b>It can't be changed once added</b>, because every day logged against it is scored with it.
      </p>
      <label className="field">
        <span className="label">Other names (optional, comma-separated)</span>
        <input
          className="input"
          value={aliases}
          placeholder="e.g. meadows row, landmine rows"
          onChange={(e) => setAliases(e.target.value)}
        />
      </label>
      <Problems problems={problems} />
      {done && <p className="sub">Added {done}. It is in the picker and the bot now.</p>}
      <div className="actions">
        <button className="btn btn-primary" type="button" disabled={saving || name.trim().length < 2} onClick={add}>
          {saving ? "Adding…" : "Add exercise"}
        </button>
      </div>
    </div>
  );
}

function RenameExercise({ uid, edits }: { uid: string; edits: CatalogEdits }) {
  const revision = useCatalogRevision();
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<CatalogExercise | null>(null);

  const needle = normalise(search.trim());
  const matches = useMemo(
    () => (needle.length < 2 ? [] : CATALOG.filter((e) => normalise(e.name).includes(needle)).slice(0, 8)),
    // `revision` stands for CATALOG, which is read by reference.
    [needle, revision],
  );

  return (
    <div className="admin-block">
      <h3 className="label">Rename an exercise</h3>
      {picked ? (
        <RenameForm
          key={picked.id}
          uid={uid}
          exercise={CATALOG.find((e) => e.id === picked.id) ?? picked}
          edits={edits}
          onClose={() => setPicked(null)}
        />
      ) : (
        <>
          <input
            className="input"
            type="search"
            value={search}
            placeholder="Find the exercise to rename"
            aria-label="Find the exercise to rename"
            onChange={(e) => setSearch(e.target.value)}
          />
          {matches.length > 0 && (
            <ul className="picker-list admin-matches">
              {matches.map((e) => (
                <li key={e.id}>
                  <button className="picker-item" type="button" onClick={() => setPicked(e)}>
                    <span>{e.name}</span>
                    <span className="sub">{GROUP_NAMES[e.muscleGroup]}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function RenameForm({
  uid,
  exercise,
  edits,
  onClose,
}: {
  uid: string;
  exercise: CatalogExercise;
  edits: CatalogEdits;
  onClose: () => void;
}) {
  const custom = isCustomId(exercise.id);
  const stored = edits.added.find((e) => e.id === exercise.id);
  const original = BUILT_IN_CATALOG.find((e) => e.id === exercise.id)?.name;
  const renamed = !custom && edits.renamed[exercise.id] !== undefined;

  const [name, setName] = useState(exercise.name);
  const [aliases, setAliases] = useState((stored?.aliases ?? []).join(", "));
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const prepared = prepareRename(exercise.id, name, custom ? splitNames(aliases) : []);
    if (!prepared.ok) {
      setProblems(prepared.problems);
      return;
    }
    setSaving(true);
    const result = await renameExercise(uid, exercise.id, prepared.name, prepared.aliases);
    setSaving(false);
    if (!result.ok) {
      setProblems([result.reason === "failed" ? result.detail : "Could not save."]);
      return;
    }
    onClose();
  };

  const restore = async () => {
    setSaving(true);
    const result = await restoreExerciseName(exercise.id);
    setSaving(false);
    if (!result.ok) setProblems([result.reason === "failed" ? result.detail : "Could not restore."]);
    else onClose();
  };

  return (
    <>
      <label className="field">
        <span className="label">New name</span>
        <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
      </label>
      {custom && (
        <label className="field">
          <span className="label">Other names (comma-separated)</span>
          <input className="input" value={aliases} onChange={(e) => setAliases(e.target.value)} />
        </label>
      )}
      <p className="sub">
        {custom
          ? "Added here."
          : renamed
            ? `Built in, originally “${original}”. The original name still finds it.`
            : "Built in. Its current name will still find it after a rename."}{" "}
        Factor {exercise.bodyweightFactor}, {exercise.measure === "duration" ? "timed" : "reps"} — fixed.
      </p>
      <Problems problems={problems} />
      <div className="actions">
        <button className="btn btn-primary" type="button" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button className="btn" type="button" disabled={saving} onClick={onClose}>
          Cancel
        </button>
        {renamed && (
          <button className="btn" type="button" disabled={saving} onClick={restore}>
            Restore “{original}”
          </button>
        )}
      </div>
    </>
  );
}

function AddedList({ edits }: { edits: CatalogEdits }) {
  if (edits.added.length === 0 && Object.keys(edits.renamed).length === 0) return null;
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  return (
    <div className="admin-block">
      <h3 className="label">Changed from here</h3>
      <ul className="admin-changes">
        {[...edits.added].sort(byName).map((e) => (
          <li key={e.id}>
            <b>{e.name}</b>{" "}
            <span className="sub">
              added · {GROUP_NAMES[e.muscleGroup]} · factor {e.bodyweightFactor}
              {e.measure === "duration" ? " · timed" : ""}
              {e.aliases.length > 0 ? ` · also “${e.aliases.join("”, “")}”` : ""}
            </span>
          </li>
        ))}
        {Object.entries(edits.renamed)
          .map(([id, name]) => ({ id, name, was: BUILT_IN_CATALOG.find((e) => e.id === id)?.name ?? id }))
          .sort(byName)
          .map((r) => (
            <li key={r.id}>
              <b>{r.name}</b> <span className="sub">renamed from {r.was}</span>
            </li>
          ))}
      </ul>
    </div>
  );
}
